import React, { useState, useEffect } from "react";
import axios from "axios";
import { DndContext, DragEndEvent, DragOverlay, useSensor, useSensors, PointerSensor } from "@dnd-kit/core";
import { Button, Space, Typography, Tabs, Breadcrumb, Input, Modal } from "antd";
import {
	UploadOutlined, DownloadOutlined, DeleteOutlined, CheckCircleOutlined,
	CloseCircleOutlined, PlusOutlined, EditOutlined, LinkOutlined, FolderOutlined,
	FileOutlined, HomeOutlined,
} from "@ant-design/icons";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { FileMetadata } from "../../../../types/file/FileTypes";
import { PatientResourceLink } from "../../../../types/file/ResourceLinkTypes";
import { listUserFiles, downloadFile, deleteFile, updateClinicalOnly, createFolder, moveItem } from "../../../../api/file/fileServiceApi";
import { getPatientResourceLinks, deletePatientResourceLink } from "../../../../api/file/patientResourceLinksApi";
import UploadFileModal from "./submodals/UploadFileModal";
import ResourceLinkModal from "./submodals/ResourceLinkModal";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";
import InlineLoading from "../../../../components/shared/InlineLoading";
import StatusTag from "../../../../components/shared/StatusTag";
import { useNotifications } from "../../../../context/NotificationContext";
import { DataTable } from "../../../../components/shared";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

interface FilesTabProps {
	patient: Patient;
	setErrorMessages: React.Dispatch<React.SetStateAction<any[]>>;
}

const FilesTab: React.FC<FilesTabProps> = ({ patient, setErrorMessages }) => {
	const [files, setFiles] = useState<FileMetadata[]>([]);
	const [links, setLinks] = useState<PatientResourceLink[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
	const [showResourceLinkModal, setShowResourceLinkModal] = useState<boolean>(false);
	const [editingResourceLink, setEditingResourceLink] = useState<PatientResourceLink | null>(null);
	const [activeTab, setActiveTab] = useState<string>("files");
	const [companyId, setCompanyId] = useState<number>(0);
	const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
	const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string | null; name: string }>>([{ id: null, name: "Home" }]);
	const [draggedItem, setDraggedItem] = useState<FileMetadata | null>(null);
	const [newFolderName, setNewFolderName] = useState<string>("");
	const [showFolderModal, setShowFolderModal] = useState<boolean>(false);
	const { showSuccess, showError, showWarning } = useNotifications();

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

	const PAGE_SIZE = loadUserPreferences().tableRowsPerPage || 10;

	useEffect(() => { fetchData(); }, [patient.id, currentFolderId]);

	const fetchData = async () => {
		setLoading(true);
		try {
			const response = await listUserFiles(patient.id, currentFolderId || undefined);
			setFiles(response);
			const linksData = await getPatientResourceLinks(patient.id);
			setLinks(linksData);
			const company = await fetchClinicCompany();
			setCompanyId(company.id);
		} catch (error) {
			handleErrors(error);
		} finally {
			setLoading(false);
		}
	};

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response) {
			if (error.response.data?.errors) {
				setErrorMessages(Object.entries(error.response.data.errors).flatMap(([_, messages]) => messages as string[]));
			} else if (error.response.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) { showWarning("Please enter a folder name"); return; }
		try {
			await createFolder(patient.id, newFolderName, currentFolderId || undefined);
			showSuccess("Folder created successfully");
			setNewFolderName("");
			setShowFolderModal(false);
			fetchData();
		} catch (error) { handleErrors(error); }
	};

	const handleFolderClick = (folder: FileMetadata) => {
		setCurrentFolderId(folder.id);
		setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.folder_name || folder.file_name }]);
	};

	const handleBreadcrumbClick = (index: number) => {
		const next = breadcrumbs.slice(0, index + 1);
		setBreadcrumbs(next);
		setCurrentFolderId(next[next.length - 1].id);
	};

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) { setDraggedItem(null); return; }
		const draggedFile = files.find((f) => f.id === active.id);
		const targetFile = files.find((f) => f.id === over.id);
		if (!draggedFile) { setDraggedItem(null); return; }
		try {
			if (targetFile?.is_folder) {
				await moveItem(draggedFile.id, patient.id, targetFile.id);
				showSuccess(`Moved to ${targetFile.folder_name}`);
			} else if (over.id === "root") {
				await moveItem(draggedFile.id, patient.id, undefined);
				showSuccess("Moved to root folder");
			}
			fetchData();
		} catch (error) { handleErrors(error); }
		setDraggedItem(null);
	};

	const handleDeleteFile = async (fileId: string) => {
		try {
			await deleteFile(fileId, patient.id);
			showSuccess("Deleted successfully");
			fetchData();
		} catch (error) { handleErrors(error); }
	};

	const handleDownloadFile = async (fileId: string) => {
		try { await downloadFile(patient.id, fileId); }
		catch (error) { handleErrors(error); }
	};

	const formatFileSize = (bytes: number) => {
		const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
		if (bytes === 0) return "0 Bytes";
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
	};

	const handleToggleClinicalOnly = async (fileid: string, clinicalOnly: boolean) => {
		try { await updateClinicalOnly(fileid, !clinicalOnly, patient.id); fetchData(); }
		catch (error) { handleErrors(error); }
	};

	const fileColumns = [
		{
			title: "Name", key: "name",
			render: (record: FileMetadata) => (
				<Space style={{ cursor: record.is_folder ? "pointer" : "default" }} onClick={() => record.is_folder && handleFolderClick(record)}>
					{record.is_folder ? <FolderOutlined style={{ color: "var(--mg-warning)" }} /> : <FileOutlined />}
					<Text>{record.is_folder ? record.folder_name : record.file_name}</Text>
				</Space>
			),
		},
		{
			title: "Date", key: "created_at",
			render: (record: FileMetadata) => {
				if (record.created_at instanceof Date) return record.created_at.toISOString().substring(0, 10);
				if (typeof record.created_at === "string") return record.created_at.substring(0, 10);
				return "";
			},
		},
		{
			title: "Clinical Only", dataIndex: "clinical_only", key: "clinical_only",
			render: (clinical_only: boolean, record: FileMetadata) => (
				<Space>
					{clinical_only
						? <Button type="text" icon={<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />} onClick={() => handleToggleClinicalOnly(record.id, clinical_only)} />
						: <Button type="text" danger icon={<CloseCircleOutlined style={{ color: "var(--mg-danger)", }} />} onClick={() => handleToggleClinicalOnly(record.id, clinical_only)} />}
				</Space>
			),
		},
		{ title: "Size", key: "file_size", render: (record: FileMetadata) => record.is_folder ? "—" : formatFileSize(record.file_size) },
		{
			title: "Actions", key: "actions",
			fixed: "right" as const,
			render: (record: FileMetadata) => (
				<Space>
					{!record.is_folder && <Button type="default" icon={<DownloadOutlined />} onClick={() => handleDownloadFile(record.id)} />}
					<Button type="default" danger icon={<DeleteOutlined />} onClick={() => handleDeleteFile(record.id)} />
				</Space>
			),
		},
	];

	const linkColumns = [
		{ title: "Resource Type", dataIndex: "resource_type", key: "resource_type", sorter: (a: PatientResourceLink, b: PatientResourceLink) => a.resource_type.localeCompare(b.resource_type) },
		{ title: "Description", dataIndex: "description", key: "description", ellipsis: true },
		{
			title: "URL", dataIndex: "link_url", key: "link_url",
			render: (url: string) => <Button type="link" icon={<LinkOutlined />} onClick={() => window.open(url, "_blank", "noopener,noreferrer")} style={{ padding: 0 }}>Open Link</Button>,
		},
		{
			title: "Status", dataIndex: "is_active", key: "is_active",
			render: (is_active: boolean) => <StatusTag status={is_active ? "active" : "inactive"} />,
		},
		{
			title: "Actions", key: "actions",
			render: (record: PatientResourceLink) => (
				<Space>
					<Button type="default" icon={<EditOutlined />} onClick={() => { setEditingResourceLink(record); setShowResourceLinkModal(true); }} />
					<Button type="default" danger icon={<DeleteOutlined />} onClick={async () => {
						try {
							await deletePatientResourceLink(record.id);
							showSuccess("Resource link deleted");
							const refreshed = await getPatientResourceLinks(patient.id);
							setLinks(refreshed);
						} catch (error) { handleErrors(error); }
					}} />
				</Space>
			),
		},
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				tabBarExtraContent={{
					right: (
						<Space>
							{activeTab === "files" && (
								<>
									<Button icon={<FolderOutlined />} onClick={() => setShowFolderModal(true)}>New Folder</Button>
									<Button type="primary" icon={<UploadOutlined />} onClick={() => setShowUploadModal(true)}>Upload Files</Button>
								</>
							)}
							{activeTab === "links" && (
								<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingResourceLink(null); setShowResourceLinkModal(true); }}>Add Resource Link</Button>
							)}
						</Space>
					),
				}}
				items={[
					{
						key: "files",
						label: "Files",
						children: (
							<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
								<div style={{ padding: "6px 12px" }}>
									<Breadcrumb>
										{breadcrumbs.map((crumb, index) => (
											<Breadcrumb.Item key={crumb.id || "root"}>
												<span onClick={() => handleBreadcrumbClick(index)} style={{ cursor: "pointer", color: "var(--mg-primary)" }}>
													{index === 0 ? <HomeOutlined /> : crumb.name}
												</span>
											</Breadcrumb.Item>
										))}
									</Breadcrumb>
								</div>

								<>
									{loading ? (
										<InlineLoading message="Loading files…" minHeight={200} />
									) : (
										<DndContext sensors={sensors} onDragEnd={handleDragEnd} onDragStart={(e) => { const item = files.find((f) => f.id === e.active.id); setDraggedItem(item || null); }}>
											<DataTable
												columns={fileColumns}
												dataSource={files}
												rowKey="id"
												pagination={{ pageSize: PAGE_SIZE }}
											/>
											<DragOverlay>
												{draggedItem ? (
													<div style={{ padding: "8px 16px", background: "var(--mg-bg-elevated)", border: "1px solid var(--mg-border)", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
														<Space>
															{draggedItem.is_folder ? <FolderOutlined /> : <FileOutlined />}
															{draggedItem.is_folder ? draggedItem.folder_name : draggedItem.file_name}
														</Space>
													</div>
												) : null}
											</DragOverlay>
										</DndContext>
									)}
								</>
							</div>
						),
					},
					{
						key: "links",
						label: "Resource Links",
						children: (
							<>
								{loading ? (
									<InlineLoading message="Loading links…" minHeight={200} />
								) : (
									<DataTable
										columns={linkColumns}
										dataSource={links}
										rowKey={(r) => String(r.id)}
										pagination={{ pageSize: PAGE_SIZE }}
									/>
								)}
							</>
						),
					},
				]}
			/>

			{showUploadModal && (
				<UploadFileModal patientId={patient.id} parentId={currentFolderId || undefined} onClose={() => setShowUploadModal(false)} onFilesUploaded={() => { setShowUploadModal(false); fetchData(); }} />
			)}

			{showResourceLinkModal && (
				<ResourceLinkModal
					visible={showResourceLinkModal}
					onClose={() => { setShowResourceLinkModal(false); setEditingResourceLink(null); }}
					onResourceLinkSaved={async () => { const r = await getPatientResourceLinks(patient.id); setLinks(r); setShowResourceLinkModal(false); }}
					patientId={patient.id}
					companyId={companyId}
					editingLink={editingResourceLink}
				/>
			)}

			<Modal title="Create New Folder" open={showFolderModal} onOk={handleCreateFolder} onCancel={() => { setShowFolderModal(false); setNewFolderName(""); }} okText="Create" destroyOnClose>
				<Input placeholder="Folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onPressEnter={handleCreateFolder} />
			</Modal>
		</div>
	);
};

export default FilesTab;